import { Button, Card, Flex, Heading, Spinner, Text } from "@radix-ui/themes";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { useMemo, useRef, useState } from "react";
import {
  useLazyStaffStoreAppDetailsQuery,
  useStaffStoresQuery,
} from "../../../api";
import { useListZonesQuery } from "../../../api/cart-api";
import { updateGlobalAPIBaseURLs } from "../../../api/axios/api-instances";
import { refreshAccessToken } from "../../../api/helpers/refresh-token";
import { getAppKey } from "../../../shared/lib/helpers";
import {
  convertServicesToEnvironment,
  type MobileCheckoutEnvironment,
} from "../../../shared/lib/environment";
import type {
  KioskBindingV1,
  KioskOperator,
  KioskPlacement,
} from "../model/kiosk-binding";
import { createKioskBinding } from "../model/kiosk-binding";

type SelectedStore = {
  key: string;
  name: string;
  services: MobileCheckoutEnvironment;
};

const nativeSelectClassName =
  "h-12 w-full appearance-none rounded-[max(var(--radius-3),var(--radius-full))] bg-[var(--color-surface)] px-4 pr-10 text-base text-[var(--gray-12)] shadow-[inset_0_0_0_1px_var(--gray-a7)] outline-none hover:shadow-[inset_0_0_0_1px_var(--gray-a8)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-[var(--focus-8)] disabled:bg-[var(--gray-a2)] disabled:text-[var(--gray-a11)] disabled:shadow-[inset_0_0_0_1px_var(--gray-a6)]";

export function StaffSetup({
  previousBinding,
  operator,
  onComplete,
  onCancel,
}: {
  previousBinding: KioskBindingV1 | null;
  operator: KioskOperator | null;
  onComplete: (binding: KioskBindingV1) => void;
  onCancel: () => void;
}) {
  const appKey = getAppKey();
  const storesQuery = useStaffStoresQuery({ app_key: appKey });
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const [selectedStore, setSelectedStore] = useState<SelectedStore | null>(
    null,
  );
  const [storeKey, setStoreKey] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [placement, setPlacement] = useState<KioskPlacement | "">("");
  const [loadingStore, setLoadingStore] = useState(false);
  const [restoringPreviousStore, setRestoringPreviousStore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storeSelectionGeneration = useRef(0);
  const activeStoreSelection = useRef<Promise<void> | null>(null);
  const zonesQuery = useListZonesQuery(undefined, { skip: !selectedStore });

  const selectedZone = useMemo(
    () => zonesQuery.data?.results.find((zone) => zone.id === zoneId),
    [zoneId, zonesQuery.data],
  );

  const selectStore = (storeKey: string) => {
    const store = storesQuery.data?.results.find(
      (item) => item.key === storeKey,
    );
    if (!store) return Promise.resolve();

    const generation = ++storeSelectionGeneration.current;
    setStoreKey(storeKey);
    setLoadingStore(true);
    setError(null);
    setSelectedStore(null);
    setZoneId("");
    setPlacement("");

    const selection = (async () => {
      try {
        const appDetails = await getAppDetails({
          store_key: storeKey,
          app_key: appKey,
        }).unwrap();
        if (generation !== storeSelectionGeneration.current) return;

        await refreshAccessToken({ storeKey, reason: "manual" });
        if (generation !== storeSelectionGeneration.current) return;

        const services = convertServicesToEnvironment(
          appDetails.result.services,
        );
        updateGlobalAPIBaseURLs(services, storeKey);
        setSelectedStore({ key: store.key, name: store.name, services });
      } catch {
        if (generation === storeSelectionGeneration.current) {
          setError("This account cannot initialize the selected store.");
        }
      } finally {
        if (generation === storeSelectionGeneration.current) {
          setLoadingStore(false);
        }
      }
    })();

    activeStoreSelection.current = selection;
    return selection;
  };

  const cancel = async () => {
    ++storeSelectionGeneration.current;
    setRestoringPreviousStore(true);

    try {
      await activeStoreSelection.current;
      if (previousBinding) {
        updateGlobalAPIBaseURLs(
          previousBinding.services,
          previousBinding.storeKey,
        );
        await refreshAccessToken({
          storeKey: previousBinding.storeKey,
          reason: "manual",
        });
      }
      onCancel();
    } catch {
      setError("The previous store could not be restored. Please try again.");
      setRestoringPreviousStore(false);
    }
  };

  const complete = () => {
    if (!selectedStore || !selectedZone || !placement) return;
    onComplete(
      createKioskBinding({
        previous: previousBinding,
        storeKey: selectedStore.key,
        storeName: selectedStore.name,
        zoneId: selectedZone.id,
        zoneName: selectedZone.description ?? selectedZone.id,
        placement,
        services: selectedStore.services,
        configuredBy: operator,
      }),
    );
  };

  return (
    <main className="h-dvh bg-gray-2 flex items-center justify-center p-6">
      <Card className="w-full max-w-[760px] !p-7 shadow-xl">
        <Heading mb="2">Configure kiosk</Heading>
        <Text as="p" color="gray" mb="6">
          Mobile App Checkout is the only supported kiosk mode.
        </Text>
        <Flex gap="5">
          <Flex className="min-w-[400px]" direction="column" gap="4">
            <label>
              <Text as="div" size="2" weight="medium" mb="1">
                Store
              </Text>
              <div className="relative">
                <select
                  className={`${nativeSelectClassName} ${storeKey ? "" : "text-[var(--gray-a10)]"}`}
                  value={storeKey}
                  onChange={(event) => void selectStore(event.target.value)}
                  disabled={loadingStore || restoringPreviousStore}
                >
                  <option value="" disabled>
                    Select store
                  </option>
                  {storesQuery.data?.results.map((store) => (
                    <option value={store.key} key={store.key}>
                      {store.name}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-1/2 size-[11px] -translate-y-1/2 text-[var(--gray-11)]"
                />
              </div>
            </label>
            {loadingStore && <Spinner />}
            <label>
              <Text as="div" size="2" weight="medium" mb="1">
                Zone
              </Text>
              <div className="relative">
                <select
                  className={`${nativeSelectClassName} ${zoneId ? "" : "text-[var(--gray-a10)]"}`}
                  value={zoneId}
                  onChange={(event) => setZoneId(event.target.value)}
                  disabled={!selectedStore || zonesQuery.isFetching}
                >
                  <option value="" disabled>
                    Select active zone
                  </option>
                  {zonesQuery.data?.results.map((zone) => (
                    <option value={zone.id} key={zone.id}>
                      {zone.description ?? zone.id}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-1/2 size-[11px] -translate-y-1/2 text-[var(--gray-11)]"
                />
              </div>
            </label>
            <label>
              <Text as="div" size="2" weight="medium" mb="1">
                Placement
              </Text>
              <div className="relative">
                <select
                  className={`${nativeSelectClassName} ${placement ? "" : "text-[var(--gray-a10)]"}`}
                  value={placement}
                  onChange={(event) =>
                    setPlacement(event.target.value as KioskPlacement)
                  }
                  disabled={!zoneId}
                >
                  <option value="" disabled>
                    Select placement
                  </option>
                  <option value="enter">Entrance</option>
                  <option value="exit">Exit</option>
                </select>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute right-4 top-1/2 size-[11px] -translate-y-1/2 text-[var(--gray-11)]"
                />
              </div>
            </label>
            {error && (
              <Text color="red" role="alert">
                {error}
              </Text>
            )}
          </Flex>
          <Flex mt="2" justify="between" direction="column" className="w-full">
            <Button
              radius="large"
              className="!basis-1/2 w-full !text-xl"
              size="4"
              disabled={
                restoringPreviousStore ||
                !selectedStore ||
                !selectedZone ||
                !placement
              }
              onClick={complete}
            >
              Start kiosk
            </Button>
            <Button
              radius="large"
              className="w-full !basis-1/3 !text-xl"
              size="4"
              variant="soft"
              color="gray"
              disabled={restoringPreviousStore}
              onClick={() => void cancel()}
            >
              {restoringPreviousStore ? "Restoring..." : "Cancel"}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </main>
  );
}
